import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { IDP_IS_A_STAND_IN } from '../config';
import {
  addPasskey,
  listPasskeys,
  passkeysSupported,
  removePasskey,
  type Passkey,
} from '../data/passkeys';
import {
  changePassword,
  confirmPassword,
  freshAccessToken,
  session,
  SignInError,
} from '../data/session';

/**
 * `/settings` — the signed-in clinician's own login: who they are signed in
 * as, their password, and their passkeys.
 *
 * Everything here goes to Cognito directly with a short-lived access token;
 * none of it touches the API or any patient data. Against the local IdP there
 * is no Cognito to talk to, so the page says so rather than offering buttons
 * that cannot work.
 */
export function Settings(): JSX.Element {
  const me = session();

  return (
    <div className="settings">
      <h1 className="settings__title">Settings</h1>

      <section className="admin__section">
        <h2 className="admin__title">Your login</h2>
        <dl className="settings__facts">
          <dt>Email address</dt>
          <dd>{me?.email}</dd>
          <dt>Role</dt>
          <dd>{me?.role === 'owner' ? 'Owner' : 'Clinician'}</dd>
        </dl>
      </section>

      {IDP_IS_A_STAND_IN ? (
        <p className="admin__note">
          Password changes and passkeys need the real sign-in service, so they are not available
          against the local identity provider.
        </p>
      ) : (
        <>
          <Passkeys />
          <ChangePassword />
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- passkeys */

function Passkeys(): JSX.Element {
  const [passkeys, setPasskeys] = useState<readonly Passkey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const supported = passkeysSupported();

  const load = useCallback(async () => {
    try {
      setPasskeys(await listPasskeys(await freshAccessToken()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your passkeys.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const accessToken = await confirmPassword(password);
      await addPasskey(accessToken);
      setAdding(false);
      setPassword('');
      await load();
    } catch (err) {
      setError(
        err instanceof SignInError
          ? err.message
          : err instanceof Error && err.name === 'NotAllowedError'
            ? 'The passkey was not created — the prompt was cancelled or timed out.'
            : err instanceof Error && err.name === 'InvalidStateError'
              ? 'This device already has a passkey for your login.'
              : 'Could not add a passkey. Try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (passkey: Passkey): Promise<void> => {
    if (removing !== null) return;
    setRemoving(passkey.id);
    setError(null);
    try {
      await removePasskey(await freshAccessToken(), passkey.id);
      await load();
    } catch {
      setError('Could not remove that passkey. Try again in a moment.');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <section className="admin__section">
      <h2 className="admin__title">Passkeys</h2>
      <p className="admin__sub">
        Sign in with your fingerprint, face or device PIN instead of typing a password. Your
        password keeps working as well.
      </p>

      {error !== null && (
        <p className="admin__error" role="alert">
          {error}
        </p>
      )}

      {passkeys === null && error === null && <p className="admin__note">Loading…</p>}

      {passkeys !== null && passkeys.length === 0 && (
        <p className="admin__note">No passkeys yet.</p>
      )}

      {passkeys !== null && passkeys.length > 0 && (
        <ul className="settings__list">
          {passkeys.map((passkey) => (
            <li key={passkey.id}>
              <span>
                <strong>{passkey.name}</strong>
                {passkey.createdAt !== null && (
                  <span className="settings__meta">
                    {' '}
                    &middot; added{' '}
                    {passkey.createdAt.toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="ghost"
                disabled={removing !== null}
                onClick={() => void remove(passkey)}
              >
                {removing === passkey.id ? 'Removing…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!supported ? (
        <p className="admin__note">This browser does not support passkeys.</p>
      ) : adding ? (
        <form className="settings__form" onSubmit={(event) => void add(event)}>
          <label className="invite__field">
            <span className="invite__label">Confirm your password first</span>
            <input
              className="invite__input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
            <span className="invite__hint">
              So that a screen left signed in is not enough to add a way into your account.
            </span>
          </label>
          <div className="reset__actions">
            <button type="submit" className="primary" disabled={busy || password === ''}>
              {busy ? 'Waiting for your device…' : 'Continue'}
            </button>
            <button
              type="button"
              className="ghost"
              disabled={busy}
              onClick={() => {
                setAdding(false);
                setPassword('');
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="primary" onClick={() => setAdding(true)}>
          Add a passkey
        </button>
      )}
    </section>
  );
}

/* -------------------------------------------------------------- password */

const MIN_PASSWORD_LENGTH = 12;

function ChangePassword(): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(`The new password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (next !== again) {
      setError('The two new passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setAgain('');
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="admin__section">
      <h2 className="admin__title">Password</h2>

      {error !== null && (
        <p className="admin__error" role="alert">
          {error}
        </p>
      )}
      {done && (
        <p className="reset__done" role="status">
          Password changed.
        </p>
      )}

      <form className="settings__form" onSubmit={(event) => void submit(event)}>
        <label className="invite__field">
          <span className="invite__label">Current password</span>
          <input
            className="invite__input"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            disabled={busy}
          />
        </label>
        <label className="invite__field">
          <span className="invite__label">New password</span>
          <input
            className="invite__input"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={next}
            onChange={(event) => setNext(event.target.value)}
            disabled={busy}
          />
          <span className="invite__hint">At least {MIN_PASSWORD_LENGTH} characters.</span>
        </label>
        <label className="invite__field">
          <span className="invite__label">New password again</span>
          <input
            className="invite__input"
            type="password"
            autoComplete="new-password"
            required
            value={again}
            onChange={(event) => setAgain(event.target.value)}
            disabled={busy}
          />
        </label>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </section>
  );
}
